// API
import api from '../../api'

// Constants
import { DisclosureType, KeyConstants } from '../../constants'

// Router
import router from '../../router'

// Utilities
import analyticsUtil from '../../utility/analyticsUtil.js'
import gpsUtil from '../../utility/gpsUtil.js'
import Logger from '../../utility/loggerUtil'
import isEmpty from '../../utility/isEmpty'
import preferences from '../../utility/preferenceUtil.js'
import waitUtil from '../../utility/waitUtil.js'

export default {

  async getRegistrationCounties (context) {
    try {
      const countyList = await api.applicationVehicleInfo.getRegistrationCounties(context.rootState.applicationCustomer.state)
      const counties = [{ value: '', text: '' }]
      countyList.counties.forEach((county) => {
        counties.push({
          text: county,
          value: county
        })
      })
      context.commit('updateRegistrationCountyList', { counties })
      return counties
    } catch (error) {
      Logger.error('Error on applicationSession/getRegistrationCounties')
      Logger.debug(error)
      throw error
    }
  },

  async getProductConfiguration (context, payload) {
    try {
      const configuration = await api.config.getProductConfiguration(payload.state, payload.productType, context.rootState.applicationRetailStore.number).catch(e => {
        Logger.warn('Retry getting application product config settings')
        Logger.debug(e)

        return api.config.getProductConfiguration(payload.state, payload.productType, context.rootState.applicationRetailStore.number).catch(e => {
          Logger.error('Error getting application product config settings')
          Logger.debug(e)
        })
      })

      if (isEmpty(configuration?.productConfiguration) || configuration.productConfiguration.error) {
        return
      }

      context.commit('updateProductConfiguration', { productConfiguration: configuration.productConfiguration })
      context.commit('applicationRetailStore/updateDefaultStoreNumber', { storeNumber: configuration.productConfiguration.defaultStoreNumber }, { root: true })
      context.commit('brands/setBrandedLinks', configuration.brandedAppUrls, { root: true })

      return configuration
    } catch (e) {
      const error = e.response && e.response.data && e.response.data.error ? e.response.data.error : e.message
      Logger.error('Error on applicationSession/getProductConfiguration')
      Logger.debug(error)
      throw new Error(error)
    }
  },

  async getDisclosures (context, payload) {
    try {
      const disclosuresResponse = await api.config.getDisclosures(payload.state, context.rootState.applicationCustomer.loanProduct, DisclosureType.application, context.rootState.applicationRetailStore.number).catch(e => {
        Logger.error('Error getting disclosures, trying again...')
        Logger.debug(e)
        return api.config.getDisclosures(payload.state, context.rootState.applicationCustomer.loanProduct, DisclosureType.application, context.rootState.applicationRetailStore.number)
      })

      if (isEmpty(disclosuresResponse)) {
        return
      }

      context.commit('updateDisclosureLinks', { disclosuresLinks: disclosuresResponse.disclosures })

      return disclosuresResponse
    } catch (e) {
      const error = e.response && e.response.data && e.response.data.error ? e.response.data.error : e.message
      Logger.error('Error on applicationSession/getDisclosures')
      Logger.debug(error)
      throw new Error(error)
    }
  },

  async getGeolocation (context, data) {
    if (!context.state.config.remoteConfigsSaved) { // Attempt to pull latest config settings before checking geolocation feature flag
      await context.dispatch('session/getConfigSettings', undefined, { root: true }).catch(e => {
        Logger.error('Error getting configs')
        Logger.debug(e)
      })
    }

    if (!context.state.config.geolocationCheckEnabled) {
      Logger.info('Skipping geolocation checks')
      return
    }

    if (!isEmpty(data.customerState) && !isEmpty(data.nextPageRoute)) {
      context.commit('setGeolocationSession', { customerState: data.customerState, nextPageRoute: data.nextPageRoute })
    }

    try {
      const geolocationPermissions = preferences.get(KeyConstants.gpsPermissionRequested, '')

      if (isEmpty(geolocationPermissions)) {
        Logger.debug('GPS permissions not asked for yet')

        return { name: 'LocationPermissionConfirm' }
      }
    } catch (e) {
      Logger.error('Error on applicationSession/getGeolocation getting GPS permission setting')
      Logger.debug(e)
      throw e
    }

    let isDenied = false
    let position

    try {
      waitUtil.waitStart('getGeolocation.GetCurrentPosition')
      position = await gpsUtil.GetCurrentPosition()

      if (isEmpty(position) || isEmpty(position.latitude) || isEmpty(position.longitude)) {
        isDenied = true
        context.commit('updateGeolocation', { geolocationEnabled: false })
      } else {
        context.commit('updateGeolocation', { geolocationEnabled: true, latitude: position.latitude, longitude: position.longitude })
      }
    } catch (e) {
      Logger.error('Error on applicationSession/getGeolocation getting GPS permission setting')
      Logger.debug(e)

      isDenied = true
    } finally {
      waitUtil.waitEnd('getGeolocation.GetCurrentPosition')
    }

    const currentPage = router.currentRoute.name

    if (isDenied) {
      Logger.warn('GPS lookup failed')
      analyticsUtil.logEvent('share', { method: 'geolocation', content_type: 'deny', item_id: `permission_${currentPage}` })

      throw new Error('PermissionBlocked')
    } else {
      analyticsUtil.logEvent('share', { method: 'geolocation', content_type: 'allow', item_id: `permission_${currentPage}`, value: `${position.latitude},${position.longitude}` })
    }

    try {
      waitUtil.waitStart('getGeolocation.locationValidate')
      const response = await api.applicationCustomer.locationValidate(context.rootState.applicationCustomer.applicationId, context.state.geolocation.customerState, position.latitude, position.longitude)

      if (!response.isValid || (!isEmpty(context.rootState.applicationCustomer.idState) && context.rootGetters['applicationCustomer/isGeolocatedIdType'] && context.rootState.applicationCustomer.idState !== context.state.geolocation.customerState)) {
        Logger.warn('Location validation failed')
        return { name: 'LocationPermissionBlocked' }
      }

      context.commit('updateGeolocation', { geolocationAddress: response.userAddress })
    } catch (e) {
      Logger.error('Error on applicationSession/getGeolocation validating location')
      Logger.debug(e)
      throw e
    } finally {
      waitUtil.waitEnd('getGeolocation.locationValidate')
    }
  },

  async getRaceConfigurations () {
    try {
      const raceConfigurations = await api.config.getVergentConfigurations()

      return raceConfigurations
    } catch (e) {
      const error = e.response?.data?.error ? e.response.data.error : e.message
      Logger.error('Error on applicationSession/getDisclosures')
      Logger.debug(error)
      throw new Error(error)
    }
  }
}
